import type { WidgetResponseSpec, AdaptedData, DataPoint } from "./types";

const DEFAULT_HEX = "#CCCCCC";

function stripYear(s: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s.slice(5) : s;
}

/**
 * Unify categories across all series and align data.
 * When backend hex !== DEFAULT_HEX, produces {y, color} point objects
 * so Highcharts renders per-point colors natively.
 */
export function adaptResponse(widgetResponse: WidgetResponseSpec, skipSort = false): AdaptedData {
  const categories: string[] = [];
  const seen = new Set<string>();
  const labelMap = new Map<string, string>();
  const positionMap = new Map<string, string>();
  const seriesDataMap = new Map<string, { name: string; points: DataPoint[] }>();

  for (const qr of widgetResponse.series) {
    for (const dp of qr.data) {
      if (!seen.has(dp.category)) {
        seen.add(dp.category);
        categories.push(dp.category);
      }
      if (dp.category_label && !labelMap.has(dp.category)) {
        labelMap.set(dp.category, dp.category_label);
      }
      if (dp.position != null && !positionMap.has(dp.category)) {
        positionMap.set(dp.category, dp.position);
      }
    }
  }

  if (!skipSort) categories.sort();

  for (const qr of widgetResponse.series) {
    const pointMap = new Map(qr.data.map((dp) => [dp.category, dp]));
    const hasColor = qr.data.some((dp) => dp.hex != null);

    const points: DataPoint[] = categories.map((cat) => {
      const dp = pointMap.get(cat);
      // A null value is an empty time bucket (showAllDatesInRange): it holds the
      // category on the axis but contributes no point, so averages stay unchanged.
      if (!dp || dp.value == null) return null;
      const hasVL = dp.value_label != null;
      // A player-identity point always takes the object branch with an explicit flag:
      // Highcharts updates points in place, so a point that turned into a bare number
      // would keep a stale flag.
      const isPlayer = dp.injured !== undefined || dp.position != null;
      if (hasColor || hasVL || isPlayer) {
        return {
          y: dp.value,
          ...(hasColor && { color: dp.hex! }),
          ...((hasVL || isPlayer) && {
            custom: {
              ...(hasVL && { value_label: dp.value_label }),
              ...(isPlayer && { injured: dp.injured === true }),
            },
          }),
        };
      }
      return dp.value;
    });

    seriesDataMap.set(qr.query_id, { name: qr.name, points });
  }

  const labels = categories.map((cat) => stripYear(labelMap.get(cat) ?? cat));

  return { categories, labels, seriesDataMap, positionMap };
}
